import {
  InsideStoryblokPreviewContextValue,
  StoryblokBlockListTransformer,
  StoryblokComponentTransformer,
  StoryblokContentReplaceTransformer,
  StoryblokFieldDelimiterTransformer,
} from "@made-people/grease-storyblok";
import { GreaseGrowthbookExperimentMatcherProps } from "@made-people/grease-growthbook";

/**
 * Create a list of Storyblok component transformers for the Miss Mary frontend.
 */
export const createStoryblokTransformers = (
  insideStoryblokPreview: InsideStoryblokPreviewContextValue,
): StoryblokComponentTransformer[] => [
  new StoryblokFieldDelimiterTransformer("_"),

  // Copy "filename" to "src" for asset fields
  new StoryblokContentReplaceTransformer(
    (value) =>
      // Remove invalid assets from content, or add src if filename is present
      !value.filename ? undefined : { ...value, src: value.filename },
    (value) => value.fieldtype === "asset",
  ),

  // Unpack from an object gearsResolvedRichText to a string
  new StoryblokContentReplaceTransformer(
    (value) => value.gearsResolvedRichText,
    (value) => "gearsResolvedRichText" in value,
  ),

  new StoryblokContentReplaceTransformer(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => {
      value.component = "AlgoliaSearchResultsProvider";
      if (value.categoryUri) {
        return {
          ...value,
          payload: {
            algoliaRequestOptions: {
              facetFilters: [`categories.uri:${value.categoryUri}`],
            },
            disjunctiveFacets: ["*"],
          },
        };
      } else if (value.categoryId) {
        return {
          ...value,
          payload: {
            algoliaRequestOptions: {
              facetFilters: [`categories.id:${value.categoryId}`],
            },
            disjunctiveFacets: ["*"],
          },
        };
      }
      return value;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => value.component === "AlgoliaSearchProvider",
  ),

  // Add href to http:// url fields, this should maybe be
  // handled in bff?
  new StoryblokContentReplaceTransformer(
    (value) => ({
      ...value,
      href: value.url,
    }),
    (value) => value?.fieldtype === "multilink" && value?.linktype === "url",
  ),

  new StoryblokContentReplaceTransformer(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => {
      const isInsideStoryblokPreview =
        typeof insideStoryblokPreview === "function"
          ? insideStoryblokPreview(value)
          : insideStoryblokPreview;

      try {
        const data = JSON.parse(value.experimentJsonAsString);

        // Turn off the forceDisplay if we are outside the storyblok preview
        const forceDisplay = isInsideStoryblokPreview
          ? value.forceDisplay
          : undefined;

        return {
          ...value,
          experimentId: data.experimentId,
          variationId: data.variationId,
          forceDisplay,
        } satisfies GreaseGrowthbookExperimentMatcherProps;
      } catch (e) {
        console.warn(
          `Failed to JSON.parse GreaseGrowthbookExperimentContainer experimentJsonAsString`,
          value,
          e,
        );
      }
      return value;
    },
    (value) => value.component === "GreaseGrowthbookExperimentContainer",
  ),

  new StoryblokContentReplaceTransformer(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => {
      if (
        typeof value.payload?.products === "string" &&
        value.payload?.products.length > 0
      ) {
        return {
          ...value,
          payload: {
            ...value.payload,
            products: value.payload.products
              .split(",")
              .map((id: string) => id.trim())
              .map(Number)
              .filter((id: number) => !isNaN(id))
              .map(String)
              .filter((id: string) => id !== ""),
          },
        };
      }
      return { ...value, payload: { ...value.payload, products: [] } };
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => value.component === "CentraSearchResultsProvider",
  ),

  // Transform SizeGuideTable: map "table" field to "metricCharts" array
  new StoryblokContentReplaceTransformer(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => {
      return {
        ...value,
        metricCharts: value.table ? [{ content: value.table }] : [],
      };
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => value.component === "SizeGuideTable",
  ),

  // Replace "children" with rendered react elements
  new StoryblokBlockListTransformer("children", insideStoryblokPreview),
];
